import axios from "axios"
import { useEffect, useState } from "react"
import { Button, Col, Container, Form, Row, Spinner } from "react-bootstrap"
import { toast } from "react-toastify"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronLeft, faLock } from "@fortawesome/free-solid-svg-icons"
import { Link, Navigate, useLocation } from "react-router-dom"
import { StartFlow } from "./StartFlow"
import { EmailOtp } from "./EmailOtp"
import { Passkey } from "./Passkey"

type Challenge = "email-otp" | "webauthn"

export interface SignInProps {
  clientId: string
  redirectUri: string
  codeChallenge: string
}

export const SignIn = () => {
  const [redirecting, setRedirecting] = useState(false)
  const [email, setEmail] = useState("")
  // const [otpCode, setOtpCode] = useState("")
  const [session, setSession] = useState<string | null>(null)
  const [webauthnParams, setWebauthnParams] = useState<any | null>(null)
  const [challenge, setChallenge] = useState<Challenge | null>(null)

  const location = useLocation()

  const clientId: string = location.state?.clientId
  const redirectUri: string = location.state?.redirectUri
  const codeChallenge: string = location.state?.codeChallenge
  const state: string = location.state?.state

  const startSignIn = async (email: string, preferredChallenge?: "email-otp" | "webauthn") => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/sign-in/start`, {
        email,
        clientId,
        preferredChallenge: preferredChallenge ?? "webauthn"
      })

      const data = response.data

      setEmail(email)
      setSession(data.session)

      if (data.challenge['emailOtp']) {
        setChallenge("email-otp")
      } else if (data.challenge['webAuthn']) {
        setChallenge("webauthn")
        setWebauthnParams(data.challenge.webAuthn.parameters)
      }
    } catch (e) {
      console.error(e)
      toast.error("Unexpected error")
    }
  }

  const sendEmailOtp = async (code: string) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/sign-in/challenges/email-otp/response`, {
        email,
        clientId,
        redirectUri,
        codeChallenge,
        codeChallengeMethod: "S256",
        session,
        code
      })

      redirectSuccess(response.data.authorizationCode)
    } catch (error) {
      console.log(error)
      if ((error as any).response?.status === 401) {
        toast.error("Invalid code")
      } else {
        console.error(error)
        toast.error("Unexpected error")
      }
    }
  }

  const sendWebAuthnResponse = async (authenticationResponse: any) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/sign-in/challenges/webauthn/response`, {
        email,
        clientId,
        redirectUri,
        codeChallenge,
        codeChallengeMethod: "S256",
        session,
        credential: authenticationResponse
      })

      redirectSuccess(response.data.authorizationCode)
    } catch (error) {
      console.log(error)
      if ((error as any).response?.status === 401) {
        toast.error("Invalid code")
      } else {
        console.error(error)
        toast.error("Unexpected error")
      }
    }
  }

  const reset = () => {
    setEmail("")
    setSession(null)
    setWebauthnParams(null)
    setChallenge(null)
  }

  const redirectSuccess = (code: string) => {
    if (process.env.REACT_APP_DISABLE_REDIRECTION === "true") {
      toast.success("Success!")
      reset()
    } else {
      setRedirecting(true)
      window.location.href = `${redirectUri}?state=${state}&code=${code}`
    }
  }

  const redirectError = () => {
    window.location.href = `${redirectUri}?state=${state}&error=access_denied`
  }

  if (clientId && redirectUri && codeChallenge) {

    return (
      <>
        <h5>Sign In</h5>
        <div>
          {(challenge === null) && <>
            <div className="mt-2 text-center">
              <StartFlow onSubmit={startSignIn} />
              <div className="fs-6 mt-2">
                <Link to="/passkeys" state={{ clientId }} className="text-decoration-none">Manage passkeys</Link>
              </div>
            </div>
          </>}
          {(challenge === "email-otp") && <EmailOtp onSubmit={sendEmailOtp} onCancel={reset} />}
          {(challenge === "webauthn") && <Passkey webAuthnParams={webauthnParams} onSuccess={sendWebAuthnResponse} onCancel={reset} onAlternativeSelection={() => startSignIn(email, "email-otp")} />}
        </div>

      </>
    )
  } else {
    return <Navigate to="/error" />
  }
}
